import React, { useState, useEffect, useCallback } from 'react';
import type { InputActionMeta, SingleValue } from 'react-select';
import { components, type OptionProps } from 'react-select';
import { useTranslation } from 'react-i18next';
import { apiClient, UserSearchResult } from '../../services/api';
import { FormCard } from '../ui/FormCard';
import { SearchSelect } from '../ui/SearchSelect';
import { CustomSelect, type SelectOption, BasicCheckbox } from '../ui';
import InviteLinkPanel from '../InviteLinkPanel';
import { TOURNAMENT_ROLES } from '@/constants';
import styles from './styles.module.scss';

interface EnhancedTournamentInviteFormProps {
  tournamentId: number;
  onInviteSent?: (result: any) => void;
  onError?: (error: string) => void;
  onClose?: () => void;
  /** When true, shows close icon in FormCard header (for inline usage instead of popup) */
  inline?: boolean;
}

type InviteTab = 'users' | 'link';

const INVITE_TABS: { id: InviteTab; labelKey: string; testId: string }[] = [
  { id: 'users', labelKey: 'tournament:inviteLink.tabUsers', testId: 'invite-tab-users' },
  { id: 'link', labelKey: 'tournament:inviteLink.tabLink', testId: 'invite-tab-link' },
];

interface SelectedUser {
  user: UserSearchResult;
  role: 'participant' | 'maintainer';
  shouldSendEmail: boolean;
}

interface UserOption {
  value: number;
  label: string;
  user: UserSearchResult;
}

const EnhancedTournamentInviteForm: React.FC<EnhancedTournamentInviteFormProps> = ({
  tournamentId,
  onInviteSent,
  onError,
  onClose,
  inline = false,
}) => {
  const { t } = useTranslation(['tournament', 'common']);

  const ROLE_OPTIONS: SelectOption[] = [
    { value: TOURNAMENT_ROLES.PARTICIPANT, label: t('tournament:participantRole') },
    { value: TOURNAMENT_ROLES.MAINTAINER, label: t('tournament:maintainerRole') },
  ];

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<InviteTab>('users');

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const searchUsers = async () => {
      try {
        setIsSearching(true);
        const response = await apiClient.searchUsers({
          q: searchQuery,
          tournament_id: tournamentId,
          exclude: selectedUsers.map(su => su.user.id),
          limit: 15
        });
        setSearchResults(response.data.data || []);
      } catch (error) {
        console.error('Error searching users:', error);
        onError?.(t('tournament:searchError'));
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, tournamentId, selectedUsers, onError, t]);

  const addUser = useCallback((user: UserSearchResult, defaultRole: 'participant' | 'maintainer' = TOURNAMENT_ROLES.PARTICIPANT) => {
    if (selectedUsers.some(su => su.user.id === user.id)) {
      return;
    }

    const newSelectedUser: SelectedUser = {
      user,
      role: defaultRole,
      shouldSendEmail: true
    };

    setSelectedUsers(prev => [...prev, newSelectedUser]);
    setSearchQuery('');
    setSearchResults([]);
  }, [selectedUsers]);

  const removeUser = (userId: number) => {
    setSelectedUsers(prev => prev.filter(su => su.user.id !== userId));
  };

  const updateUserRole = (userId: number, role: 'participant' | 'maintainer') => {
    setSelectedUsers(prev =>
      prev.map(su =>
        su.user.id === userId ? { ...su, role } : su
      )
    );
  };

  const updateEmailSetting = (userId: number, shouldSendEmail: boolean) => {
    setSelectedUsers(prev =>
      prev.map(su =>
        su.user.id === userId ? { ...su, shouldSendEmail } : su
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedUsers.length === 0) {
      onError?.(t('tournament:selectAtLeastOneUser'));
      return;
    }

    try {
      setIsSubmitting(true);

      const emailInvitations = selectedUsers.filter(su => su.shouldSendEmail);
      const directInvitations = selectedUsers.filter(su => !su.shouldSendEmail);

      const results: any[] = [];

      if (emailInvitations.length > 0) {
        // Group email invitations by role to send separate requests per role
        const byRole = emailInvitations.reduce<Record<string, string[]>>((acc, su) => {
          const r = su.role || TOURNAMENT_ROLES.PARTICIPANT;
          if (!acc[r]) acc[r] = [];
          acc[r].push(su.user.email);
          return acc;
        }, {});

        for (const [role, emails] of Object.entries(byRole)) {
          const emailResponse = await apiClient.sendBulkEmailInvitations({
            tournamentId: Number(tournamentId),
            emails,
            message: customMessage || undefined,
            role: role as 'participant' | 'maintainer'
          });

          results.push(emailResponse.data);
        }
      }

      if (directInvitations.length > 0) {
        for (const invitation of directInvitations) {
          if (invitation.user.username) {
            try {
              const usernameResponse = await apiClient.sendUsernameInvitation({
                tournamentId: Number(tournamentId),
                inviteeUsername: invitation.user.username,
                message: customMessage || undefined,
                role: invitation.role
              });

              results.push(usernameResponse.data);
            } catch (usernameError: any) {
              console.error(`Failed to send username invitation to ${invitation.user.username}:`, usernameError);
              if (invitation.user.email) {
                try {
                  const fallbackResponse = await apiClient.sendBulkEmailInvitations({
                    tournamentId: Number(tournamentId),
                    emails: [invitation.user.email],
                    message: customMessage || undefined,
                    role: invitation.role
                  });
                  results.push(fallbackResponse.data);
                } catch (emailError) {
                  console.error(`Both username and email invitations failed for user:`, invitation.user);
                  throw emailError;
                }
              } else {
                throw usernameError;
              }
            }
          } else if (invitation.user.email) {
            const emailResponse = await apiClient.sendBulkEmailInvitations({
              tournamentId: Number(tournamentId),
              emails: [invitation.user.email],
              message: customMessage || undefined,
              role: invitation.role
            });
            results.push(emailResponse.data);
          }
        }
      }

      onInviteSent?.(results);

      setSelectedUsers([]);
      setCustomMessage('');
      setSearchQuery('');

    } catch (error: any) {
      console.error('Error sending invitations:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      let errorMessage = t('tournament:errorSendingInvitations');

      if (error.response?.status === 403) {
        errorMessage = t('tournament:noPermissionToInvite');
      } else if (error.response?.status === 404) {
        errorMessage = t('tournament:tournamentNotFoundDeleted');
      } else if (error.response?.status === 409) {
        errorMessage = t('tournament:usersAlreadyInvited');
      } else if (error.response?.data?.data?.failed?.length > 0) {
        const failedCount = error.response.data.data.failed.length;
        const failedEmails = error.response.data.data.failed
          .map((f: any) => `• ${f.email}: ${f.error}`)
          .join('\n');

        errorMessage = `Не удалось отправить ${failedCount} приглашений:\n${failedEmails}`;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayResults = searchQuery.length >= 2 ? searchResults : [];

  const options: UserOption[] = displayResults.map((user) => ({
    value: user.id,
    label: user.display_name || user.email,
    user,
  }));

  const handleInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === 'input-change') {
      setSearchQuery(newValue);
    }
  };

  const handleChange = (newValue: SingleValue<UserOption>) => {
    if (newValue) {
      addUser(newValue.user);
    }
  };

  const isOptionDisabled = (option: UserOption) => {
    const user = option.user;
    return !!(user.tournament_participation?.is_participant) || selectedUsers.some(su => su.user.id === user.id);
  };

  const CustomOption = (props: OptionProps<UserOption, false>) => {
    const { data } = props;
    const user = data.user;
    const isAlreadyParticipant = user.tournament_participation?.is_participant;

    return (
      <components.Option {...props}>
        <div className={styles.optionContent}>
          <div className={styles.userInfo}>
            <div className={styles.userName}>
              {user.display_name}
              {user.username && (
                <span className={styles.userHandle}>(@{user.username})</span>
              )}
            </div>
            <div className={styles.userEmail}>{user.email}</div>
            {isAlreadyParticipant && (
              <div className={styles.participantBadge}>
                {t('tournament:alreadyParticipant')}
              </div>
            )}
          </div>
        </div>
      </components.Option>
    );
  };

  const noOptionsMessage = () => {
    if (searchQuery.length < 2) return t('tournament:minSearchChars');
    return t('tournament:usersNotFound');
  };

  return (
    <FormCard
      title={t('tournament:inviteParticipants')}
      titleSize="sm"
      onClose={inline ? onClose : undefined}
      className={styles.card}
    >
      <div className={`tab-nav ${styles.tabs}`} role="group" aria-label={t('tournament:inviteLink.tabsLabel')}>
        {INVITE_TABS.map(({ id, labelKey, testId }) => (
          <button
            key={id}
            type="button"
            className={`btn btn--sm ${activeTab === id ? 'active' : ''}`}
            aria-pressed={activeTab === id}
            onClick={() => setActiveTab(id)}
            data-testid={testId}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>

      {activeTab === 'link' ? (
        <InviteLinkPanel tournamentId={Number(tournamentId)} />
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* User Search */}
          <div className={styles.searchSection}>
            <label className={styles.label}>
              {t('tournament:findUsers')}
            </label>
            <SearchSelect<UserOption>
              inputValue={searchQuery}
              onInputChange={handleInputChange}
              onChange={handleChange}
              options={options}
              isLoading={isSearching}
              isOptionDisabled={isOptionDisabled}
              filterOption={() => true}
              blurInputOnSelect={false}
              placeholder={t('tournament:searchUsersPlaceholder')}
              noOptionsMessage={noOptionsMessage}
              components={{ Option: CustomOption }}
              value={null}
            />
          </div>

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div className={styles.selectedSection}>
              <label className={styles.label}>
                {t('tournament:selectedUsers', { count: selectedUsers.length })}
              </label>
              {selectedUsers.map((selectedUser) => (
                <div key={selectedUser.user.id} className={styles.selectedItem}>
                  <div className={styles.selectedHeader}>
                    <div className={styles.selectedName}>
                      {selectedUser.user.display_name}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUser(selectedUser.user.id)}
                      className={styles.removeBtn}
                    >
                      &#10005;
                    </button>
                  </div>

                  <div className={styles.selectedActions}>
                    <div className={styles.roleSelect}>
                      <CustomSelect
                        options={ROLE_OPTIONS}
                        value={ROLE_OPTIONS.find(o => o.value === selectedUser.role) || ROLE_OPTIONS[0]}
                        onChange={(val) => {
                          if (val) updateUserRole(selectedUser.user.id, val.value as 'participant' | 'maintainer');
                        }}
                        variant="minimal"
                        isSearchable={false}
                      />
                    </div>

                    <div className={styles.emailToggle}>
                      <BasicCheckbox
                        id={`email-${selectedUser.user.id}`}
                        color="grass"
                        checked={selectedUser.shouldSendEmail}
                        onChange={(e) => updateEmailSetting(selectedUser.user.id, e.target.checked)}
                      />
                      <label htmlFor={`email-${selectedUser.user.id}`}>Email</label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Custom Message */}
          <div className={styles.messageSection}>
            <label htmlFor="customMessage" className={styles.label}>
              {t('tournament:personalMessage')}
            </label>
            <textarea
              id="customMessage"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={t('tournament:addPersonalMessage')}
              className="field"
            />
            <div className={styles.charCount}>
              {t('tournament:characters', { count: customMessage.length })}
            </div>
          </div>

          {/* Submit buttons */}
          <div className={styles.footer}>
            <div className={styles.footerInfo}>
              {selectedUsers.length > 0 && (
                <>
                  {t('tournament:invitationsWillBeSent', { count: selectedUsers.filter(su => su.shouldSendEmail).length })}
                </>
              )}
            </div>

            <div className={styles.footerActions}>
              {onClose && (
                <button type="button" onClick={onClose} className="btn btn--sm btn--outlined">
                  {t('common:cancel')}
                </button>
              )}
              <button
                type="submit"
                disabled={selectedUsers.length === 0 || isSubmitting}
                className="btn btn--sm"
              >
                {t('tournament:invite')}
              </button>
            </div>
          </div>
        </form>
      )}
    </FormCard>
  );
};

export default EnhancedTournamentInviteForm;
